import { ARTIFACT_IDS, ROLE_IDS, ROLES, type RoleId } from '@onuw/roles-data'
import { Fragment } from 'react'
import { Link } from 'react-router'
import { RoleLink } from '../RichText'
import { R, TokenList } from './parts'

const MARK_ROLES: RoleId[] = ROLE_IDS.filter((id) => ROLES[id].tags.includes('marks'))
const NUMBER_ROLES: RoleId[] = ROLE_IDS.filter((id) => ROLES[id].tags.includes('needsNumbers'))

const roleLinks = (roles: RoleId[]) =>
  roles.map((r, i) => (
    <Fragment key={r}>
      {i > 0 && ', '}
      <RoleLink id={r} />
    </Fragment>
  ))

export function MarksAndArtifacts() {
  return (
    <>
      <p>
        Cards are not the only things on the table. Three kinds of token lie beside them: the marks of the
        Vampire pack, the artifacts of the <R id="curator" /> and the shield of the <R id="sentinel" />. A
        card says what you are; a token can change that, or change what your death means.
      </p>

      <h2>Marks</h2>
      <p>
        Marks are in play when the deck holds any of these roles: {roleLinks(MARK_ROLES)}. Every player then
        starts with a face-down <strong>Mark of Clarity</strong> beside their card, and never has more than
        one mark: a new one replaces the old.
      </p>
      <TokenList
        marks={[
          'markOfClarity',
          'markOfVampire',
          'markOfFear',
          'markOfBat',
          'markOfLove',
          'markOfDisease',
          'markOfTheTraitor',
          'markOfAssassin',
        ]}
      />

      <h3>How marks are handed out</h3>
      <p>
        Marks are given at <strong>dusk</strong>, before the night. The dusk roles act in{' '}
        <Link to="/guides/night-order/">wake order</Link>: the Vampires, the <R id="count">Count</R>,{' '}
        <R id="renfield" />, the <R id="diseased" />, <R id="cupid" />, the <R id="instigator" />, the{' '}
        <R id="priest" />, the <R id="assassin" /> and the <R id="appAssassin" />. When two of them mark the
        same player, the later mark stays. That is the Priest’s whole job: his Mark of Clarity wipes whatever
        the Vampires, the Count, Cupid or the Instigator put there, and only the Assassins mark after him.
      </p>
      <p>
        When dusk ends, everyone looks at their own mark, and the players with a Mark of Love wake and see
        each other. From then on a mark can still change hands without its player knowing: the{' '}
        <R id="pickpocket" /> trades his mark for another player’s, and the <R id="gremlin" /> may swap two
        players’ marks. The <R id="marksman" /> may look at one. What counts in the vote is the mark in front
        of you when the night ends.
      </p>

      <h3>Rulings</h3>
      <ul>
        <li>
          <strong>Mark of the Vampire.</strong> Its player is a Vampire: they win with the Vampires, and the
          village may win by killing them. It wins over the <R id="cursed" />
          ’s change, and loses to an artifact that changes the player’s role.
        </li>
        <li>
          <strong>Mark of Fear.</strong> A player who sees it on their own mark takes no night action. Dusk
          actions have already happened and stand. The night still pauses for the feared role, so nobody can
          tell.
        </li>
        <li>
          <strong>Mark of Love.</strong> When one lover dies, so does the other, even a <R id="prince" /> or a
          player the <R id="bodyguard" /> protects, and it chains with the <R id="hunter" />
          ’s shot. Love links deaths, not teams. A single Mark of Love does nothing.
        </li>
        <li>
          <strong>Mark of Disease.</strong> Everyone who voted for its player loses, whatever their team did.
          The player with the mark can still die and can still win.
        </li>
        <li>
          <strong>Mark of the Traitor.</strong> Its player wins only if someone else on their own team dies. A
          player alone on their team is not affected: the only Werewolf at the table, the <R id="blob" />, the{' '}
          <R id="mortician" />, an Assassin.
        </li>
        <li>
          <strong>Mark of the Assassin.</strong> The Assassin wins if its player dies; the Apprentice Assassin
          wins if the Assassin dies, or without one, if the marked player does. With no such mark in play,
          both are plain villagers.
        </li>
        <li>
          <strong>A shield does not cover a mark.</strong> A shielded player can still be marked, have their
          mark stolen, or have it looked at.
        </li>
      </ul>

      <h2>Artifacts</h2>
      <p>
        The Curator may put one artifact, drawn at random, in front of any player who has none, himself
        included. It lies face down, and its player alone looks at it when the night ends. Six of the nine
        change what that player is for the vote, whatever card they hold.
      </p>
      <TokenList artifacts={ARTIFACT_IDS} />
      <ul>
        <li>
          An artifact stays with the player, not with the card: it is placed near the end of the night, after
          the cards have moved.
        </li>
        <li>The Curator does not see what he gave. Only the player with the artifact knows.</li>
        <li>
          The <strong>Mask of Muting</strong> and the <strong>Shroud of Shame</strong> are rules for the
          people at the table: the app does not silence anyone, so the player keeps to them.
        </li>
        <li>A shielded player can still be given an artifact.</li>
      </ul>

      <h2>The shield</h2>
      <p>
        The Sentinel may put the shield on another player’s card early in the night. For the rest of the night
        nobody may look at that card or move it: the <R id="robber" /> cannot take it, the{' '}
        <R id="troublemaker" /> cannot swap it, the <R id="villageIdiot" />
        ’s shift goes around it, and a shielded <R id="insomniac" /> cannot even look at her own card.
      </p>

      <h2>Player numbers</h2>
      <p>
        With any of these roles in the deck ({roleLinks(NUMBER_ROLES)}), every player gets a number in seat
        order. The app’s instructions use it: "view any even player", "swap with a player with a higher
        number". The <R id="oracle" /> may be asked whether hers is odd or even, with the whole table
        listening.
      </p>
    </>
  )
}
